/**
 * The review window: one feedback item in a large centred window.
 *
 * Inline-start pane — everything about the feedback (rating, reasons, the full
 * comment, who, where, which model, when, skills and tools), the other feedback
 * on the same conversation (each jumps to its answer) and, once shown, the
 * conversation's files. Inline-end pane — the conversation itself in the chat's
 * own read-only components, opened straight at the rated answer, fetched once
 * per conversation (`chatPane.ts`). An organization whose message content is
 * switched off shows only the feedback, never the conversation.
 * In Hebrew the panes mirror with the page.
 *
 * Previous / next step through the list without closing; Esc or the close
 * button close; focus stays inside and returns to the list.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Select, Spin } from 'antd';
import {
  CopyOutlined, DownloadOutlined, FileOutlined, GoogleOutlined,
  LeftOutlined, LockOutlined, RightOutlined, RobotOutlined,
} from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { bidiIsolate } from '@/utils/bidi';
import { activeLocale } from '@/utils/locale';
import showNotification from '@/utils/showNotification';
import { MessageList } from '@modules/conversations/components/MessageList';
import { messageFromApi } from '@modules/conversations/services/conversationService';
import feedbackReviewService from '../../services/feedbackReviewService';
import type { FeedbackConversation, FeedbackDetail, FeedbackFile, FeedbackItem } from '../../types/feedback';
import { askKey, chatPaneStep } from './chatPane';
import { PersonAvatar, RatingBadge, ReasonPills, formatWhen } from './feedbackParts';
import { UsedCapabilities } from './UsedCapabilities';
import '@modules/chatbots/components/BotModal.css';
import '@modules/chatbots/components/ConnectorDialog.css';
import '@modules/conversations/pages/ChatPage.css';

const HIGHLIGHT_MS = 2300;
const CLOSE_MS = 160;

type Stage = 'loading' | 'ready' | 'content_off' | 'not_found' | 'failed';

interface Props {
  items: FeedbackItem[];
  /** The open item's place in the list; null when opened from a link. */
  index: number | null;
  /** A copied link's feedback id, when the item is not (yet) in the list. */
  linkedId: string | null;
  hasMore: boolean;
  /** How many items match the filters (more than are loaded while paging). */
  total: number;
  onLoadMore: () => void;
  onIndexChange: (index: number) => void;
  onClose: (lastFeedbackId: string | null) => void;
}

/** A file's size in the reader's language — the unit as Intl writes it. */
function formatSize(bytes: number): string {
  // Past 1,023.5 kB it reads as 1 MB, never "1,024 kB".
  const [value, unit] = bytes < 1024 ? [bytes, 'byte']
    : bytes < 1023.5 * 1024 ? [bytes / 1024, 'kilobyte'] : [bytes / (1024 * 1024), 'megabyte'];
  return new Intl.NumberFormat(activeLocale(), {
    style: 'unit', unit, unitDisplay: 'short', maximumFractionDigits: unit === 'megabyte' ? 1 : 0,
  }).format(value as number);
}

export function FeedbackWindow({ items, index, linkedId, hasMore, total, onLoadMore, onIndexChange, onClose }: Props) {
  const { t } = useLanguage();
  const listItem = index !== null ? items[index] ?? null : null;
  const feedbackId = listItem?.feedback_id ?? linkedId;

  const [detail, setDetail] = useState<FeedbackDetail | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [stage, setStage] = useState<Stage>('loading');
  const [shown, setShown] = useState<FeedbackConversation | null>(null);
  const [jumpTo, setJumpTo] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [versions, setVersions] = useState<Record<string, number>>({});
  const [busyFile, setBusyFile] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const requestRef = useRef(0);
  // The last ask (`askKey`): an effect that runs again for the same item must
  // not fetch the conversation a second time.
  const askedRef = useRef<string | null>(null);

  // The fetched record only once it is THIS item's: right after a step it is
  // still the previous one's.
  const item: FeedbackItem | null = (detail?.feedback_id === feedbackId ? detail : null) ?? listItem;
  const conversationId = item?.conversation_id ?? null;

  // The item's own record (no message content; nothing is recorded).
  useEffect(() => {
    if (!feedbackId) return undefined;
    const ctrl = new AbortController();
    setDetailFailed(false);
    setDetail((d) => (d?.feedback_id === feedbackId ? d : null));
    feedbackReviewService.detail(feedbackId, ctrl.signal)
      .then((d) => setDetail(d))
      .catch((err) => { if (err?.name !== 'CanceledError') setDetailFailed(true); });
    return () => ctrl.abort();
  }, [feedbackId]);

  const load = useCallback(async () => {
    if (!conversationId) return;
    const request = ++requestRef.current;
    askedRef.current = askKey(conversationId, feedbackId);
    setStage('loading');
    try {
      const payload = await feedbackReviewService.openConversation(conversationId);
      if (request !== requestRef.current) return;
      setShown(payload);
      setStage('ready');
    } catch (err: any) {
      if (request !== requestRef.current) return;
      const code = err?.response?.data?.error_code;
      setStage(code === 'CONTENT_SWITCHED_OFF' ? 'content_off'
        : err?.response?.status === 404 ? 'not_found' : 'failed');
    }
  }, [conversationId, feedbackId]);

  // The conversation opens with the item; within the same conversation it stays.
  useEffect(() => {
    setJumpTo(item?.message_id ?? null);
    const next = chatPaneStep(item, shown?.conversation.conversation_id ?? null);
    if (next === 'keep') return;
    // Already asked for this very item (still asking, or answered).
    if (next === 'load' && askedRef.current === askKey(conversationId, feedbackId)) return;
    requestRef.current += 1;
    askedRef.current = null;
    setShown(null);
    if (next === 'wait') { setStage('loading'); return; }
    if (next === 'content_off') { setStage('content_off'); return; }
    void load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, feedbackId, item?.content_allowed]);

  const messages = useMemo(() => (shown?.messages || []).map(messageFromApi), [shown]);
  const markers = useMemo(() => {
    const out: Record<string, { likes: number; dislikes: number }> = {};
    for (const f of shown?.feedback || []) {
      const m = out[f.message_id] || (out[f.message_id] = { likes: 0, dislikes: 0 });
      if (f.rating === 'like') m.likes += 1;
      else m.dislikes += 1;
    }
    return out;
  }, [shown]);

  // Open at the rated answer, ringed; the same for a jump from the list.
  useEffect(() => {
    if (stage !== 'ready' || !jumpTo) return undefined;
    const handle = window.setTimeout(() => {
      const el = scrollerRef.current?.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(jumpTo)}"]`);
      if (!el) return;
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      el.classList.remove('message-group--review-highlight');
      void el.offsetWidth;
      el.classList.add('message-group--review-highlight');
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => el.classList.remove('message-group--review-highlight'), HIGHLIGHT_MS);
    }, 80);
    return () => window.clearTimeout(handle);
  }, [stage, jumpTo, shown]);

  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current); }, []);

  const close = useCallback(() => {
    if (closing) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    setClosing(true);
    window.setTimeout(() => onClose(feedbackId ?? null), reduce ? 0 : CLOSE_MS);
  }, [closing, onClose, feedbackId]);

  // Focus moves in, and stays in, while the window is open.
  useEffect(() => { closeRef.current?.focus(); }, []);

  // Escape and Tab are handled on the document, wherever focus is: a control
  // that goes away (a step button just disabled, a retry replaced by the
  // transcript) drops focus to <body>, outside the dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') {
        // An open antd popup (the version picker) closes first.
        if (document.querySelector('.ant-select-dropdown:not(.ant-select-dropdown-hidden)')) return;
        close();
        return;
      }
      const dialog = dialogRef.current;
      if (e.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      )).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const inside = dialog.contains(document.activeElement);
      if (!inside || (e.shiftKey && document.activeElement === first)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  const canPrev = index !== null && index > 0;
  const canNext = index !== null && (index < items.length - 1 || hasMore);
  const step = (delta: number) => {
    if (index === null) return;
    const next = index + delta;
    if (next < 0) return;
    if (next >= items.length) {
      if (hasMore) onLoadMore();
      return;
    }
    if (next >= items.length - 3 && hasMore) onLoadMore();
    onIndexChange(next);
    if (next === 0 || (next === items.length - 1 && !hasMore)) closeRef.current?.focus();
  };

  const copyLink = async () => {
    if (!feedbackId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'feedback');
    url.searchParams.set('feedback', feedbackId);
    try {
      await navigator.clipboard.writeText(url.toString());
      showNotification(t('insights.feedback.actions.linkCopied'), 'success');
    } catch {
      showNotification(t('insights.feedback.actions.linkCopyFailed'), 'error');
    }
  };

  const fileFor = (attachmentId: string) => shown?.files.find((f) => f.attachment_id === attachmentId);
  const getFile = async (file: FeedbackFile, open: boolean) => {
    if (!conversationId) return;
    if (file.kind === 'drive') {
      if (file.drive_link) window.open(file.drive_link, '_blank', 'noopener');
      return;
    }
    setBusyFile(file.attachment_id);
    try {
      await feedbackReviewService.getFile(conversationId, file.attachment_id, {
        version: file.kind === 'artifact' ? (versions[file.attachment_id] ?? file.versions[0]?.version ?? null) : null,
        fallbackName: file.file_name,
        open,
      });
    } catch {
      showNotification(t('insights.feedback.files.failed'), 'error');
    } finally {
      setBusyFile(null);
    }
  };

  // The transcript's messages are memoised without their callbacks, so they
  // get one stable function that reads the CURRENT feedback, versions and files.
  const transcriptOpen = useRef<(attachmentId: string) => void>(() => undefined);
  transcriptOpen.current = (attachmentId: string) => {
    const file = fileFor(attachmentId);
    if (file) getFile(file, true);
  };
  const openFromTranscript = useCallback((attachmentId: string) => transcriptOpen.current(attachmentId), []);

  const title = item?.agent || t('insights.feedback.noAgent');
  const subtitle = [
    item?.organization ? bidiIsolate(item.organization) : null,
    detail?.conversation_title ? bidiIsolate(detail.conversation_title) : null,
  ].filter(Boolean).join(' · ');
  const current = detail?.feedback_id === feedbackId ? detail : null;
  const others = current?.conversation_feedback ?? [];

  return createPortal(
    <div
      className={`modal-overlay cxd-overlay fbw-overlay${closing ? ' is-closing' : ''}`}
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        ref={dialogRef}
        className={`modal fbw${closing ? ' is-closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fbw-title"
        data-testid="feedback-window"
      >
        <div className="modal-header fbw__header">
          <div className="modal-header-content fbw__heading">
            <h2 id="fbw-title" className="modal-title cxd__title">
              <span dir="auto">{title}</span>
              {item && <RatingBadge rating={item.rating} />}
            </h2>
            {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          </div>
          <div className="fbw__controls">
            {index !== null && (
              <span className="fbw__position">
                {t('insights.feedback.window.position', { n: index + 1, total })}
              </span>
            )}
            <button type="button" className="cx-btn cx-btn--sm fbw__step" onClick={() => step(-1)} disabled={!canPrev}
              aria-label={t('insights.feedback.window.previous')} title={t('insights.feedback.window.previous')}
              data-testid="feedback-window-prev">
              <LeftOutlined className="rtl-flip" aria-hidden />
            </button>
            <button type="button" className="cx-btn cx-btn--sm fbw__step" onClick={() => step(1)} disabled={!canNext}
              aria-label={t('insights.feedback.window.next')} title={t('insights.feedback.window.next')}
              data-testid="feedback-window-next">
              <RightOutlined className="rtl-flip" aria-hidden />
            </button>
            <button type="button" className="cx-btn cx-btn--sm" onClick={copyLink} data-testid="feedback-window-copy">
              <CopyOutlined aria-hidden /> {t('insights.feedback.actions.copyLink')}
            </button>
            <button ref={closeRef} type="button" className="modal-close-btn" onClick={close}
              aria-label={t('insights.feedback.viewer.close')} data-testid="feedback-window-close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="fbw__body">
          <aside className="fbw__facts" aria-label={t('insights.feedback.window.aboutFeedback')} data-testid="feedback-window-facts">
            {!item && !detailFailed && <div className="fbw__center"><Spin /></div>}
            {detailFailed && !item && <p className="cxs__note">{t('insights.feedback.linked.notFound')}</p>}
            {item && (
              <>
                <section className="fbw__section">
                  <div className="fbw__rated">
                    <RatingBadge rating={item.rating} />
                    <span className="fbw__when">{formatWhen(item.rated_at)}</span>
                    {item.edited && (
                      <span className="cx-pill" title={t('insights.feedback.editedTooltip', { date: bidiIsolate(formatWhen(item.first_rated_at)) })}>
                        {t('insights.feedback.edited')}
                      </span>
                    )}
                  </div>
                  <ReasonPills reasons={item.reasons} rating={item.rating} />
                </section>

                <section className="fbw__section">
                  <h3 className="fbw__label">{t('insights.feedback.expanded.comment')}</h3>
                  {item.comment
                    ? <p className="fbw__comment" dir="auto">{item.comment}</p>
                    : <p className="fbw__muted">{t('insights.feedback.window.noComment')}</p>}
                </section>

                <section className="fbw__section">
                  <dl className="fbw__facts-list">
                    <div><dt>{t('insights.feedback.expanded.ratedBy')}</dt>
                      <dd className="fbw__person"><PersonAvatar name={item.user_name} />
                        <span dir="auto">{item.user_name || t('insights.feedback.unknownUser')}</span></dd></div>
                    <div><dt>{t('insights.feedback.columns.organization')}</dt>
                      <dd dir="auto">{item.organization || '—'}</dd></div>
                    <div><dt>{t('insights.feedback.columns.agent')}</dt>
                      <dd dir="auto">{item.agent || t('insights.feedback.noAgent')}</dd></div>
                    <div><dt>{t('insights.feedback.expanded.model')}</dt>
                      <dd dir="auto" title={item.model || undefined}>{item.model_name || item.model || '—'}</dd></div>
                    {item.edited && (
                      <div><dt>{t('insights.feedback.window.firstGiven')}</dt>
                        <dd>{formatWhen(item.first_rated_at)}</dd></div>
                    )}
                  </dl>
                </section>

                {/* What this answer, and its conversation, actually used —
                    read from the turns' own records when the item opens. */}
                {current?.capabilities
                  ? <UsedCapabilities capabilities={current.capabilities} />
                  : !detailFailed && <div className="fbw__center"><Spin size="small" /></div>}

                {others.length > 1 && (
                  <section className="fbw__section">
                    <h3 className="fbw__label">
                      {t('insights.feedback.viewer.rail')} <span className="cx-pill">{others.length}</span>
                    </h3>
                    <ol className="fbw__others" data-testid="feedback-window-others">
                      {others.map((f) => (
                        <li key={f.feedback_id}>
                          <button
                            type="button"
                            className={`fbw__other${f.feedback_id === item.feedback_id ? ' is-current' : ''}`}
                            onClick={() => setJumpTo(f.message_id)}
                            aria-current={f.feedback_id === item.feedback_id ? 'true' : undefined}
                            title={t('insights.feedback.viewer.jump')}
                          >
                            <RatingBadge rating={f.rating} compact />
                            <span className="fbw__other-body">
                              <span className="fbw__other-who"><bdi dir="auto">{f.user_name || t('insights.feedback.unknownUser')}</bdi></span>
                              <span className="fbw__other-when">
                                {f.answer_number != null && (
                                  <bdi className="fbw__other-answer">{t('insights.feedback.used.answerN', { n: f.answer_number })}</bdi>
                                )}
                                {formatWhen(f.rated_at)}
                                {f.edited && (
                                  <span className="cx-pill fbw__other-edited"
                                    title={t('insights.feedback.editedTooltip', { date: bidiIsolate(formatWhen(f.first_rated_at)) })}>
                                    {t('insights.feedback.edited')}
                                  </span>
                                )}
                              </span>
                              {f.comment && <span className="fbw__other-comment bidi-clamp" dir="auto">{f.comment}</span>}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}

                <section className="fbw__section" data-testid="feedback-window-files">
                  <h3 className="fbw__label">{t('insights.feedback.files.title')}</h3>
                  {!shown && !item.content_allowed && (
                    <p className="fbw__muted">{t('insights.feedback.files.contentOff')}</p>
                  )}
                  {!shown && item.content_allowed && stage === 'loading' && <Spin size="small" />}
                  {shown && shown.files.length === 0 && <p className="fbw__muted">{t('insights.feedback.files.none')}</p>}
                  {shown && shown.files.length > 0 && (
                    <ul className="fbw__files">
                      {shown.files.map((file) => (
                        <li key={file.attachment_id} className="fbw__file" data-testid="feedback-window-file">
                          <span className="fbw__file-icon" aria-hidden="true">
                            {file.kind === 'drive' ? <GoogleOutlined /> : file.kind === 'artifact' ? <RobotOutlined /> : <FileOutlined />}
                          </span>
                          <span className="fbw__file-body">
                            <span className="fbw__file-name" dir="auto">{file.file_name}</span>
                            <span className="fbw__file-meta">
                              {t(`insights.feedback.files.kind.${file.kind}`)}
                              {file.file_size != null && ` · ${bidiIsolate(formatSize(file.file_size))}`}
                            </span>
                          </span>
                          <span className="fbw__file-actions">
                            {file.kind === 'artifact' && file.versions.length > 1 && (
                              <Select
                                size="small"
                                value={versions[file.attachment_id] ?? file.versions[0].version}
                                onChange={(v) => setVersions((s) => ({ ...s, [file.attachment_id]: v }))}
                                options={file.versions.map((v) => ({
                                  value: v.version, label: t('insights.feedback.files.version', { n: v.version }),
                                }))}
                                aria-label={t('insights.feedback.files.versionLabel')}
                                popupMatchSelectWidth={false}
                                className="fbw__version"
                              />
                            )}
                            {file.kind === 'drive' ? (
                              <button type="button" className="cx-btn cx-btn--sm" onClick={() => getFile(file, true)}
                                disabled={!file.drive_link}>
                                {t('insights.feedback.files.openInDrive')}
                              </button>
                            ) : (
                              <button type="button" className="cx-btn cx-btn--sm" onClick={() => getFile(file, false)}
                                disabled={busyFile === file.attachment_id} data-testid="feedback-window-download">
                                <DownloadOutlined aria-hidden /> {t('insights.feedback.files.download')}
                              </button>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </aside>

          <section className="fbw__chat" aria-label={t('insights.feedback.viewer.transcript')} data-testid="feedback-window-chat">
            {stage === 'loading' && <div className="fbw__center"><Spin size="large" /></div>}
            {stage === 'content_off' && (
              <div className="fbw__center fbw__off" data-testid="feedback-content-off">
                <span className="cxs__icon" aria-hidden="true"><LockOutlined /></span>
                <p className="fbr__empty-title">{t('insights.feedback.window.contentOffTitle')}</p>
                <p className="cxs__desc">{t('insights.feedback.expanded.contentOff', { org: bidiIsolate(item?.organization || '') })}</p>
              </div>
            )}
            {stage === 'not_found' && <div className="fbw__center"><p className="cxs__note">{t('insights.feedback.viewer.notFound')}</p></div>}
            {stage === 'failed' && (
              <div className="fbw__center">
                <p className="cxs__note">{t('insights.feedback.viewer.failed')}</p>
                <button type="button" className="cx-btn" onClick={() => { void load(); }}>{t('insights.feedback.error.retry')}</button>
              </div>
            )}
            {stage === 'ready' && shown && (
              <>
                <div className="fbw__chat-head">
                  <span className="fbw__chat-title" dir="auto">
                    {shown.conversation.title || t('insights.feedback.viewer.untitled')}
                  </span>
                  <span className="fbw__chat-meta">
                    <span className="cx-pill"><LockOutlined aria-hidden /> {t('insights.feedback.viewer.readOnly')}</span>
                  </span>
                </div>
                {shown.conversation.is_branch && (
                  <p className="cxs__note fbw__branch">
                    {shown.conversation.parent?.title
                      ? t('insights.feedback.viewer.branchOf', { title: shown.conversation.parent.title })
                      : t('insights.feedback.viewer.branch')}
                  </p>
                )}
                <div className="chat-messages-container fbw__scroller" ref={scrollerRef}>
                  <MessageList
                    messages={messages}
                    readOnly
                    feedbackByMessage={markers}
                    onAttachmentOpen={openFromTranscript}
                    showSkillLabels={shown.conversation.show_skill_labels}
                    revealThinking={shown.conversation.reveal_thinking}
                    botName={shown.conversation.agent || undefined}
                    botColor={(shown.conversation.bot_color as any) || undefined}
                    branchPointMessageId={shown.conversation.branch_point_message_id || undefined}
                    showSenderIdentity={!!shown.conversation.project_id}
                    conversationTitle={shown.conversation.title || undefined}
                  />
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
